import { useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SearchCriteria } from '@/types/job';

const EXAMPLES = [
  'Find every shot where a bottle with the label "Sessy" is visible',
  'Find all frames showing a red car',
  'Find scenes where the ACME logo appears on screen',
  'Find every close-up of a coffee cup with visible branding',
  'Find all shots containing a laptop with text on the screen',
];

interface JobFormProps {
  onSubmit: (criteria: SearchCriteria) => void;
  loading?: boolean;
}

export default function JobForm({ onSubmit, loading }: JobFormProps) {
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;
    onSubmit({
      mode: 'natural_language',
      naturalQuery: description.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="description" className="text-sm font-medium">
          Describe what you're looking for
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={loading}
          placeholder='e.g. Find every shot where a bottle with the label reading "Sessy" is visible'
          rows={3}
          className={[
            'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
            'ring-offset-background placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50 resize-none',
          ].join(' ')}
        />
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => setDescription(ex)}
              className="text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-2 py-1 rounded transition-colors"
            >
              {ex.length > 50 ? ex.substring(0, 50) + '...' : ex}
            </button>
          ))}
        </div>
      </div>

      <Button type="submit" disabled={!description.trim() || loading} className="w-full">
        <Search className="h-4 w-4 mr-2" />
        {loading ? 'Starting Analysis...' : 'Find & Extract Clips'}
      </Button>
    </form>
  );
}
